import { SelfContainedCommand } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityService } from '../../../services/entityService';
import { generateCitationKey } from '../types/reference';

/**
 * Quick command for AI to see what references are available
 * 
 * This is faster than search and gives AI an overview
 */
export const libraryListReferencesCommand: SelfContainedCommand = {
  id: 'library_list_references',
  description: 'Get a quick overview of all references in the Library. Returns condensed list with IDs, citation keys, titles, and years. Use this to see what\'s available before searching.',
  
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'sortBy',
        type: 'string',
        description: 'Sort order: "recent" (default), "author", "title"',
        required: false
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number to return (default 50)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { limit: 30 },
        expectedOutput: {
          status: 'success',
          message: 'Found 100 references, showing 30',
          format: 'json',
          data: { total: 100, returned: 30, references: [] }
        },
        description: 'Get overview of available references'
      }
    ],
    bad: []
  },
  
  handler: async (params, context): Promise<StandardToolResult> => {
    try {
      const { sortBy = 'recent', limit = 50 } = params;
      const { spaceId } = context;
      
      const references = await entityService.queryEntities({
        entityType: 'Reference',
        spaceId
      });
      
      // Sort
      const sorted = [...references];
      if (sortBy === 'recent') {
        sorted.sort((a, b) => {
          const dateA = new Date(a.skeleton?.addedAt || 0).getTime();
          const dateB = new Date(b.skeleton?.addedAt || 0).getTime();
          return dateB - dateA;
        });
      } else if (sortBy === 'author') {
        sorted.sort((a, b) => {
          const authorA = a.skeleton?.authors?.[0] || '';
          const authorB = b.skeleton?.authors?.[0] || '';
          return authorA.localeCompare(authorB);
        });
      } else if (sortBy === 'title') {
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      // Limit and format
      const formatted = sorted.slice(0, limit).map(ref => ({
        id: ref._id,
        citationKey: ref.skeleton?.citationKey || generateCitationKey(ref.skeleton?.authors || [], ref.skeleton?.year || 0),
        title: ref.skeleton?.title || ref.name,
        firstAuthor: ref.skeleton?.authors?.[0] || 'Unknown',
        year: ref.skeleton?.year,
        tags: ref.skeleton?.tags || []
      }));
      
      return createSuccessResult(
        `Found ${references.length} references, showing ${formatted.length}`,
        {
          total: references.length,
          returned: formatted.length,
          references: formatted
        }
      );
    } catch (error) {
      return createErrorResult(
        'Failed to list references',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

